/**
 * The letter a workspace's avatar shows.
 *
 * Shared rather than copied: the sidebar switcher and the workspace list both
 * render this mark, and the front door and the shell disagreeing about what a
 * workspace looks like is the kind of drift nobody notices until it is
 * everywhere. Falls back to a middle dot, which reads as "something" without
 * pretending to be an initial.
 */
export function workspaceInitial(name: string | undefined): string {
  return name?.trim().charAt(0).toUpperCase() || '·';
}

/** Past this many days, a count stops being easier to read than a date. */
const DAYS_BEFORE_ABSOLUTE = 30;

/** Midnight-anchored day number, so the difference counts calendar days. */
function dayNumber(date: Date): number {
  return Math.floor(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000,
  );
}

/**
 * The workspace row's second line: how recently the workspace was touched.
 *
 * This is what the row shows instead of the workspace's UUID. A UUID names
 * nothing a person recognises and is the same shape on every row, so it could
 * neither be read nor scanned; `updated_at` was already on the payload and
 * unused.
 *
 * Days are calendar days rather than 24-hour spans — something touched at
 * 23:50 last night reads as "yesterday" at 00:10, which is what a person
 * means by it.
 *
 * Returns `null` for a value that cannot be parsed, so a malformed timestamp
 * leaves the line empty rather than printing "Invalid Date" at the reader.
 */
export function formatUpdated(
  iso: string,
  now: Date,
  // "Active" on the home card (spec 201), where the moment is the last activity
  // in the workspace rather than an edit of it.
  verb: 'Updated' | 'Active' = 'Updated',
): string | null {
  const updated = new Date(iso);
  if (Number.isNaN(updated.getTime())) return null;

  const days = dayNumber(now) - dayNumber(updated);

  if (days <= 0) return `${verb} today`;
  if (days === 1) return `${verb} yesterday`;
  if (days < DAYS_BEFORE_ABSOLUTE) return `${verb} ${String(days)} days ago`;

  // en-GB rather than the runtime locale: the product's copy is English, and a
  // date whose format changes with the machine is a date two people cannot
  // discuss.
  return `${verb} on ${updated.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`;
}

/**
 * The front door's greeting, by the hour on this device's clock.
 *
 * Three words, not a weather report: the greeting exists so the page opens by
 * addressing the person rather than announcing a list, and the first name is
 * all it needs. A display name the identity read has not returned yet, or one
 * with nothing in it, leaves the greeting bare rather than saying ", undefined".
 */
export function greeting(now: Date, displayName?: string | null): string {
  const hour = now.getHours();
  const word =
    hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const first = displayName?.trim().split(/\s+/)[0];
  return first ? `${word}, ${first}` : word;
}
